import {
  AdaptedInvitation,
  AdaptedRoomInfo,
  CreateMeetingRequest,
  MeetingResponse,
  MeetingStatus,
  NewInvitationResponse,
} from './types';

export const getMeetingStatus = (meeting: MeetingResponse, now: number = Date.now()): MeetingStatus => {
  if (meeting.cancelled_at !== null) return 'cancelled';
  // Instant rooms (null ends_at) have no scheduled end — treat as upcoming until explicitly started/ended.
  if (meeting.ends_at === null) {
    return meeting.started_at !== null ? 'in_progress' : 'upcoming';
  }
  const endsAtMs = new Date(meeting.ends_at).getTime();
  if (meeting.started_at !== null && endsAtMs > now) return 'in_progress';
  if (endsAtMs <= now) return 'past';
  return 'upcoming';
};

export const adaptMeetingResponse = (response: MeetingResponse): AdaptedRoomInfo => ({
  meetingId: response.id,
  roomId: response.matrix_room_id,
  roomAlias: response.alias,
  roomName: response.room_name,
  scheduledAt: response.scheduled_at,
  endsAt: response.ends_at,
  startedAt: response.started_at,
  message: response.message,
  createdByMxid: response.created_by_mxid,
  isTeamMeeting: response.is_team_meeting,
  waitingListEnabled: response.waiting_list_enabled,
  workspaceMatrixRoomId: response.workspace_matrix_room_id,
});

export const adaptInvitationResponse = (response: NewInvitationResponse): AdaptedInvitation => ({
  meetingId: response.room?.id ?? null,
  matrixRoomId: response.room?.matrix_room_id ?? null,
  scheduledAt: response.room?.scheduled_at ?? null,
  startedAt: response.room?.started_at ?? null,
  thirdPartySigned: response.third_party_signed
    ? {
        mxid: response.third_party_signed.mxid,
        sender: response.third_party_signed.sender,
        signatures: response.third_party_signed.signatures,
      }
    : undefined,
  token: response.token,
});

interface BuildCreateMeetingPayloadOptions {
  isWaitingListEnabled?: boolean;
  /** Links the meeting to a workspace. Required for invitees who have not yet
   *  joined the Matrix room to see the meeting in the correct workspace list. */
  workspaceMatrixRoomId?: string | null;
  /** True when the meeting space is workspace-restricted (JoinRule.Restricted). */
  isTeamMeeting?: boolean;
}

export const buildCreateMeetingPayload = (
  roomId: string,
  scheduledAt?: string,
  endsAt?: string,
  message?: string,
  roomName?: string,
  alias?: string,
  addGuests?: string[],
  options: BuildCreateMeetingPayloadOptions = {}
): CreateMeetingRequest => ({
  matrix_room_id: roomId,
  // Omit scheduled_at/ends_at for instant rooms — backend stores NULL, skipping
  // scheduling emails and the cron expiry sweep (#7745, #7648).
  ...(scheduledAt !== undefined && { scheduled_at: scheduledAt }),
  ...(endsAt !== undefined && { ends_at: endsAt }),
  ...(message !== undefined && { message }),
  ...(roomName !== undefined && { room_name: roomName }),
  ...(alias !== undefined && { alias }),
  ...(addGuests && addGuests.length > 0 && { add_guests: addGuests }),
  // Mirrors the Matrix join rule into the IS so guests without a local room
  // copy get the correct pre-room UI from resolveFromBackendMeeting.
  ...(options.isWaitingListEnabled !== undefined && { waiting_list_enabled: options.isWaitingListEnabled }),
  ...(options.workspaceMatrixRoomId ? { workspace_matrix_room_id: options.workspaceMatrixRoomId } : {}),
  ...(options.isTeamMeeting !== undefined && { is_team_meeting: options.isTeamMeeting }),
});
